import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useState,
} from 'react';
import {
  ActivityIndicator, AppState, Image, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity,
  View,
} from 'react-native';
import Moment from 'moment';
import Modal from 'react-native-modal';
import Colors from '../../../constants/Colors';
import ZoomIcon from '../../24pxIcons/ZoomIcon';
import getAssetTickets from '../../../requests/Assets/GetAssetTickets';
import {
  ADDITIONAL_FIELD_TYPES,
} from '../AddTemplateModal/components/FieldList/components/FieldItem';
import AssetTagSection from './components/AssetTagSection';
import getSolveTags from '../../../requests/Assets/GetSolveTags';
import TicketsRaisedSection from './components/TicketsRaisedSection';
import AssetCardField from './components/AssetCardField';
import NoteSection from './components/NoteSection';
import { logEvent } from '../../LogEvent';
import AssetCardLink from './components/AssetCardLink';
import AssetCardLocationField from './components/AssetCardLocationField';
import { AssetInstance, Template, AssetCustomField } from '../../../constants/Types';
import { PressableState } from '../../PressableStyled';
import { loadCustomFields } from '../data';

interface AssetCardMobileProps {
  tags: string[],
  setTags: Dispatch<SetStateAction<string[]>>,
  tagIndex: number,
  setTagIndex: Dispatch<SetStateAction<number>>,
  templates: Template[],
  asset: AssetInstance,
  buildingId: string,
  ticketCountRefresh: number,
  showScreenAlert,
}

export default function AssetCardMobile({
  tags, setTags, tagIndex, setTagIndex, templates, asset, buildingId, ticketCountRefresh,
  showScreenAlert,
}: AssetCardMobileProps) {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [mainShown, setMainShown] = useState<boolean>(true);
  const [ticketCounts, setTicketCounts] = useState<[number, number, number, number]>([0, 0, 0, 0]);
  const template: Template = templates
    ?.filter((prototype: Template) => asset.templateId === prototype.id)[0];
  const [loadingImage, setLoadingImage] = useState<boolean>(true);
  const [image] = useState<string>(
    asset?.imageUrl?.length > 0 ? `${asset.imageUrl}?time=${Date.now()}` : null,
  );
  const addTag = (tag: string) => setTags([...tags, tag]);

  const removeTag = (tag: string) => setTags(tags.filter((tagItem) => tagItem !== tag));

  // eslint-disable-next-line global-require
  const Placeholder = require('../../../assets/images/placeholder.png');

  const loadTicketCount = () => {
    getAssetTickets(asset.id, buildingId).then((response) => {
      if (!(response?.Tickets)) return;
      const values = response.Tickets;
      setTicketCounts([values.day ?? 0, values.week ?? 0, values.month ?? 0, values.year ?? 0]);
    });
  };

  useEffect(() => {
    getSolveTags(asset.id).then((res) => {
      setTags(res.solveTags.map((tag) => tag.solveTagId));
    });
    const listener = AppState.addEventListener('change', loadTicketCount);
    return () => listener.remove();
  }, []);

  useEffect(loadTicketCount, [ticketCountRefresh]);

  const renderTabs = () => <View style={styles.tabButtonContainer}>
    <TouchableOpacity
      style={[styles.tabButton, mainShown && styles.tabButtonSelected]}
      onPress={() => {
        logEvent('AssetCardMobile', 'Asset_Main_Info');
        setMainShown(true);
      }}
    >
      <Text style={[styles.tabButtonTitle, mainShown && styles.tabButtonTitleSelected]}>
        {'MAIN'}
      </Text>
    </TouchableOpacity>
    <TouchableOpacity
      style={[styles.tabButton, !mainShown && styles.tabButtonSelected]}
      onPress={() => {
        logEvent('AssetCardMobile', 'Asset_Details');
        setMainShown(false);
      }}
    >
      <Text style={[styles.tabButtonTitle, !mainShown && styles.tabButtonTitleSelected]}>
        {'DETAILS'}
      </Text>
    </TouchableOpacity>
  </View>;

  const renderMainView = () => <>
    <Modal
      isVisible={modalVisible}
      animationIn={'fadeIn'}
      animationOut={'fadeOut'}
      onBackButtonPress={() => setModalVisible(false)}
      useNativeDriver={true}
    >
      <TouchableOpacity
        onPress={() => {
          logEvent('AssetCardMobile', 'Modal_Image');
          setModalVisible(!modalVisible);
        }}
        style={{ height: '100%', flex: 1 }}
      >
        <Image
          key={Date.now()}
          source={image === null ? Placeholder : { uri: image }}
          resizeMode={'center'}
          style={{ width: undefined, height: '100%' }}
        />
      </TouchableOpacity>
    </Modal>
    {renderTabs()}
    <View>
      {mainShown && <View>
        <Text style={styles.sectionTitle}>{'ESSENTIAL INFO'}</Text>
        <View style={styles.underline} />
        <View style={{ flexDirection: 'row' }}>
          <Pressable
            style={{ marginBottom: 12, marginEnd: 12 }}
            onPress={() => {
              logEvent('AssetCardMobile', 'Thumbnail_Image');
              setModalVisible(true);
            }}
            disabled={image === null}
          >
            {({ hovered }: PressableState): ReactElement => (
              <View
                style={{
                  opacity: hovered ? 0.5 : 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Image
                  key={Date.now()}
                  source={image === null ? Placeholder : { uri: image }}
                  resizeMode={'cover'}
                  onLoadEnd={() => setLoadingImage(false)}
                  style={{ width: 125, height: 76 }}
                />
                {loadingImage && <ActivityIndicator style={{ position: 'absolute' }} />}
                {image !== null && (
                  <ZoomIcon
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      borderRadius: 3,
                      backgroundColor: hovered
                        ? Colors.light.hereworksBlue500
                        : `${Colors.light.hereworksBlack300}05`,
                    }}
                  />
                )}
              </View >
            )}
          </Pressable >
          <View style={{ flex: 1 }}>
            <AssetCardField
              title='Model number'
              value={asset.model}
            />
            <AssetCardField
              title='Serial number'
              value={asset.serial}
            />
          </View>
        </View >
        <View style={{ marginTop: 16 }}>
          <AssetCardField
            title='Manufacturer'
            value={asset.manufacturer}
          />
          <AssetCardField
            title='Description'
            value={asset.description}
          />
          <AssetCardField
            title='Asset ID'
            value={asset.id}
          />
          <View style={{ flexDirection: 'row' }}>
            {asset.locationArea ? <AssetCardField
              title='Area'
              value={asset.locationArea}
            /> : <></>}
            {asset.locationSpace ? <AssetCardField
              title='Space'
              value={asset.locationSpace}
            /> : <></>}
          </View>
          <View style={{ flexDirection: 'row' }}>
            <AssetCardField
              title='Floor'
              value={asset.locationFloor}
            />
          </View>
          <AssetCardLocationField
            title='View on map'
            {...{ asset }}
          />
        </View>
        <View style={{ marginTop: 16 }}>
          {template?.customfields.length > 0 && <>
            <Text style={styles.sectionTitle}>{'ADDITIONAL INFO'}</Text>
            <View style={styles.underline} />
          </>}
          {(loadCustomFields(template, asset))?.map((field: AssetCustomField, index: number) => {
            const title = field.name;
            let { value } = field;
            if (field.format === ADDITIONAL_FIELD_TYPES.DATE) {
              const date: Date = new Date(value);
              if (date instanceof Date && !Number.isNaN(date.getTime())) {
                value = Moment(date).format('DD/MM/YYYY');
              }
            }
            if (field.format === ADDITIONAL_FIELD_TYPES.LINK) {
              return <AssetCardLink key={index} {...{ title, value }} />;
            }
            return <AssetCardField key={index} {...{ title, value }} />;
          })}
        </View>
      </View >}
      {!mainShown && <>
        <AssetTagSection
          assetId={asset.id}
          {...{
            tags, addTag, removeTag, tagIndex, setTagIndex,
          }}
          showAlert={showScreenAlert}
        />
        <NoteSection
          notesArray={asset.notes ?? []}
          name={asset.name}
          id={asset.id}
        />
      </>
      }
    </View >
  </>;

  return <ScrollView>
    <>
      <View style={styles.container}>
        <View style={{ flex: 1 }}>
          <View style={{ flex: 1 }}>
            <Text style={styles.assetTitle}>{'Asset\'s Name'}</Text>
            <Text
              style={styles.assetName}
              numberOfLines={1}
              ellipsizeMode='tail'
            >
              {asset.name}
            </Text>
          </View >
          {renderMainView()}
        </View >
      </View >
      <TicketsRaisedSection {...{ ticketCounts, asset }} />
    </>
  </ScrollView >;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flex: 1,
    marginVertical: 16,
    marginHorizontal: 24,
    borderRadius: 12,
    overflow: 'hidden',
    minHeight: 340,
    backgroundColor: Colors.light.hereworksWhite100,
    padding: 16,
  },
  tabButtonTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    marginHorizontal: 12,
    color: Colors.light.hereworksBlue500,
  },
  tabButtonTitleSelected: {
    color: '#FFF',
  },
  assetName: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 16,
    lineHeight: 24,
    marginEnd: 8,
    color: Colors.light.hereworksBlack600,
  },
  sectionTitle: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  underline: {
    alignSelf: 'stretch',
    height: 1,
    backgroundColor: Colors.light.hereworksBlue100,
    marginTop: 2,
    marginBottom: 16,
  },
  tabButtonContainer: {
    flexDirection: 'row',
    marginVertical: 16,
  },
  tabButton: {
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    borderWidth: 1,
    zIndex: 2,
    backgroundColor: '#FFF',
    marginEnd: 8,
    height: 28,
    justifyContent: 'center',
  },
  tabButtonSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  assetTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
  },
});
